import { EditableInfoCard, SubmitProps } from '@/components/editable-info-card';
import { FormInputField } from '@/components/forms/form-input-field';
import { InfoKeyValue } from '@/components/info-key-value';
import { SchoolPortalBanner } from '@/components/school-portal-banner';
import { getMe, updateMe } from '@/lib/api-calls';
import { updateMeOptionsSchema } from '@airreading/common/data';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';
import { ProfileFormProps } from './profile-form';

const formSchema = updateMeOptionsSchema;
type FormState = z.infer<typeof formSchema>;

export const SchoolAdminProfileForm = ({
  toast,
  queryClient,
  setTimezone,
  getSubmitErrorMessage,
}: ProfileFormProps) => {
  const { mutateAsync } = useMutation({
    mutationFn: updateMe,
  });
  const { data: profile } = useSuspenseQuery(getMe());

  const displayName =
    profile.firstName && profile.lastName ? `${profile.firstName} ${profile.lastName}` : profile.fullNameOrEmail ?? '';

  const onSubmit = ({ formData, disableEditing }: SubmitProps<FormState>) => {
    return mutateAsync(formData)
      .then(({ timeZone }) => {
        if (timeZone) {
          setTimezone(timeZone);
        }

        toast({
          title: 'Profile updated successfully',
        });
        disableEditing();
        queryClient.invalidateQueries({ queryKey: getMe().queryKey });
      })
      .catch((e: unknown) => {
        toast({
          title: getSubmitErrorMessage(e),
        });
      });
  };

  return (
    <SchoolPortalBanner title="Basic information">
      <EditableInfoCard
        headerText={displayName}
        showRedDot={false}
        headerClass="border-b border-dashed border-border pb-4 mb-2"
        maxItemsPerRow={2}
        editButtonVariant="outline"
        formSchema={formSchema}
        defaultValues={{
          timeZone: profile.timeZone ?? Temporal.Now.timeZoneId(),
          phone: profile.phone || '',
        }}
        onSubmit={onSubmit}
        data-testid="school-admin-profile"
      >
        {({ form, editing }) => (
          <>
            <InfoKeyValue
              label="First name"
              value={profile.firstName || '-'}
              labelBold={false}
              data-testid="school-admin-profile-first-name"
            />
            <InfoKeyValue
              label="Last name"
              value={profile.lastName || '-'}
              labelBold={false}
              data-testid="school-admin-profile-last-name"
            />
            <InfoKeyValue
              label="Email"
              value={profile.email || '-'}
              labelBold={false}
              data-testid="school-admin-profile-email"
            />
            <InfoKeyValue
              label="Phone number"
              value={profile.phone || '-'}
              labelBold={false}
              data-testid="school-admin-profile-phone"
              edit={{
                editing,
                editModeComponent: <FormInputField label="Phone number" name="phone" form={form} />,
              }}
            />
          </>
        )}
      </EditableInfoCard>
    </SchoolPortalBanner>
  );
};
